import React, { useMemo, useState } from "react";
import { Autocomplete, Box, Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";
import HOMOLOGATIONS from "../../data/homologations-sui-lie.json";

/**
 * HomologationPicker — choix d'une piste homologuée FIS en Suisse et au
 * Liechtenstein (liste FIS "Homologations alpine skiing", valide au
 * 01.10.2026, fichier src/data/homologations-sui-lie.json). Ajouté le
 * 01.10.2026.
 *
 * La sélection remplit nom de la piste, n° d'homologation, altitudes de
 * départ / arrivée, dénivelé et longueur via onSelect(course) ; tous ces
 * champs restent modifiables ensuite dans le formulaire (le sélecteur ne
 * fait que pré-remplir).
 *
 * Pour mettre à jour la liste : remplacer le fichier JSON (format :
 * { courses: [{ nation, place, course, homologation, event, start,
 * finish, vd, length, validTo, status }] }).
 */

// Discipline de la course -> type d'homologation de la liste FIS.
const EVENT_FOR_DISCIPLINE = {
    SL: ["SL"],
    GS: ["GS"],
    SG: ["SG"],
    DH: ["DH"],
    AC: ["DH", "SG", "SL"],
    CR: ["GS", "SL"],
    PSL: ["PAR"],
    PGS: ["PAR"],
    PS: ["PAR"],
    PSP: ["PAR"],
};

// "01.07.2030" -> Date ; null si illisible.
function parseDate(s) {
    const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(String(s || "").trim());
    return m ? new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])) : null;
}

const optionLabel = (c) => `${c.place} — ${c.course} (${c.event}) — ${c.homologation}`;

export default function HomologationPicker({ discipline, raceDate, value, onSelect }) {
    const events = EVENT_FOR_DISCIPLINE[discipline] || null;
    const [onlyDiscipline, setOnlyDiscipline] = useState(true);

    const options = useMemo(() => {
        const all = HOMOLOGATIONS.courses || [];
        return events && onlyDiscipline ? all.filter((c) => events.includes(c.event)) : all;
    }, [events, onlyDiscipline]);

    // Piste actuellement sélectionnée = celle dont le n° correspond au champ
    // homologation du formulaire (si l'opérateur le modifie, plus de sélection).
    const selected = useMemo(
        () => (HOMOLOGATIONS.courses || []).find((c) => c.homologation === String(value || "").trim()) || null,
        [value]
    );

    const validTo = parseDate(selected?.validTo);
    const race = parseDate(raceDate);
    const expired = validTo && race && validTo < race;

    return (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <Autocomplete
                    sx={{ flex: 1, minWidth: 320 }}
                    size="small"
                    options={options}
                    value={selected}
                    groupBy={(c) => `${c.place}${c.nation !== "SUI" ? ` (${c.nation})` : ""}`}
                    getOptionLabel={optionLabel}
                    isOptionEqualToValue={(a, b) => a.homologation === b.homologation}
                    onChange={(_, c) => c && onSelect(c)}
                    renderOption={(props, c) => (
                        <li {...props} key={c.homologation}>
                            <Box>
                                <Typography variant="body2">
                                    <b>{c.course}</b> — {c.event} — {c.homologation}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {c.start} m → {c.finish} m · dénivelé {c.vd} m · {c.length} m · valide jusqu'au {c.validTo}
                                    {c.status === "Expiring" ? " (expire bientôt)" : ""}
                                </Typography>
                            </Box>
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Piste homologuée FIS (Suisse / Liechtenstein)"
                            placeholder="Lieu, piste ou n° d'homologation"
                        />
                    )}
                />
                {events && (
                    <FormControlLabel
                        control={<Checkbox size="small" checked={onlyDiscipline} onChange={(e) => setOnlyDiscipline(e.target.checked)} />}
                        label={`Seulement ${events.join(" / ")}`}
                    />
                )}
            </Box>
            {selected && (
                <Typography variant="caption" color={expired ? "error" : "text.secondary"} display="block" sx={{ mt: 0.5 }}>
                    {expired
                        ? `⚠ Homologation expirée le ${selected.validTo}, avant la date de course.`
                        : `Homologation valide jusqu'au ${selected.validTo}.`}{" "}
                    Les champs ci-dessous restent modifiables.
                </Typography>
            )}
        </Box>
    );
}